import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["actionsComponent", "columnKeys", "numberOfFrozenColumns"];
import { useEffect, memo } from 'react';
import useCrmObjectTypeObserver from 'framework-listing-lib/internal/hooks/useCrmObjectTypeObserver';
import FrameworkTable from 'framework-listing-lib/internal/Table/components/FrameworkTable';
import useCrmObjectTypeResync from 'framework-listing-lib/internal/hooks/useCrmObjectTypeResync';
import { useViews } from 'framework-listing-lib/internal/ViewTabs/providers/ViewsProvider';
import useRenderEditColumnsModal from 'framework-listing-lib/internal/EditColumns/hooks/useRenderEditColumnsModal';
import useRenderExportModal from 'framework-listing-lib/internal/Export/hooks/useRenderExportModal';
import useManageAccessObserver from 'framework-listing-lib/internal/ManageAccess/hooks/useManageAccessObserver';
import useMultiLanguageGroupObserver from 'framework-listing-lib/internal/MultiLanguageGroup/hooks/useMultiLanguageGroupObserver';
import ErrorBoundary from 'framework-listing-lib/internal/ErrorBoundary';
import { SENTRY_KEYS } from 'framework-listing-lib/utils/sentry';
import useDeepEqual from 'framework-listing-lib/internal/hooks/useDeepEqual';
import { useHasViews } from 'framework-listing-lib/internal/ViewTabs/hooks/useViewProps';
import { useHasFoldersTab } from 'framework-listing-lib/internal/Folders/hooks/useFolderProps';
import { useHasFoldersMode, useIsFoldersMode } from 'framework-listing-lib/internal/hooks/useUIMode';
import LayoutWithFoldersSidebar from 'framework-listing-lib/internal/Table/components/LayoutWithFoldersSidebar';
import { useSetNumberOfFrozenColumns } from 'framework-listing-lib/internal/ViewTabs/hooks/useViewProps';
import AsyncListingLibEditColumnsModal from 'framework-listing-lib/internal/EditColumns/components/AsyncListingLibEditColumnsModal';
import AsyncListingLibExportModal from 'framework-listing-lib/internal/Export/components/AsyncListingLibExportModal';
import DragProvider from '../providers/DragProvider';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function CrmListingTable(_ref) {
  let {
      actionsComponent,
      columnKeys: columnKeysProp,
      numberOfFrozenColumns
    } = _ref,
    tableProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const columnKeys = useDeepEqual(columnKeysProp || []);
  const hasViews = useHasViews();
  const {
    columns,
    setColumns
  } = useViews();
  const setNumberOfFrozenColumns = useSetNumberOfFrozenColumns();
  const {
    modalProps: editColumnsModalProps,
    show: showEditColumnsModal
  } = useRenderEditColumnsModal();
  const {
    modalProps: exportModalProps,
    show: showExportModal
  } = useRenderExportModal();
  const isFoldersMode = useIsFoldersMode();
  const hasFoldersTab = useHasFoldersTab();
  const hasFoldersMode = useHasFoldersMode();
  useCrmObjectTypeObserver();
  useManageAccessObserver();
  useMultiLanguageGroupObserver();
  useCrmObjectTypeResync();

  /**
   * Update columns and number of frozen columns when not in views mode.
   *
   * numberOfFrozenColumns should be set ONLY when its value is not undefined.
   * Since teams need to manually persist it value on their own, we'll only enable
   * frozen columns if the current value is present & valid.
   */
  const shouldUseColumnKeys = !hasViews || isFoldersMode;
  useEffect(() => {
    if (!shouldUseColumnKeys) {
      return;
    }
    setColumns(columnKeys || []);
    if (numberOfFrozenColumns !== undefined) {
      setNumberOfFrozenColumns(numberOfFrozenColumns);
    }
  }, [columnKeys, numberOfFrozenColumns, setColumns, setNumberOfFrozenColumns, shouldUseColumnKeys]);

  /**
   * Fix for an edge case where isLoading is false on the first render
   * (eg if the table was unmounted but the network cache wasn't)
   * in which case columns will be an empty array, because the useEffect
   * above won't have run yet.
   * Currently (2025-03-06) if columns.length === 0, the fallback is to render
   * columns for ALL properties, which is very costly
   * If we know that we want to use columnKeys, we can use them directly
   * here instead of waiting for the state to be set.
   * We still need to defer to @columns after the first render because they're
   * the source of truth for column order
   */
  const defaultedColumns = shouldUseColumnKeys && columns.length === 0 ? columnKeys : columns;
  const frameworkTable = /*#__PURE__*/_jsx(FrameworkTable, Object.assign({}, tableProps, {
    actionsComponent: actionsComponent,
    columnKeys: defaultedColumns
  }));
  return /*#__PURE__*/_jsxs(ErrorBoundary, {
    fingerprint: SENTRY_KEYS.CRM_LISTING_TABLE,
    children: [hasFoldersTab || hasFoldersMode ? /*#__PURE__*/_jsx(DragProvider, {
      children: /*#__PURE__*/_jsx(LayoutWithFoldersSidebar, {
        maxHeight: tableProps.maxHeight,
        children: frameworkTable
      })
    }) : /*#__PURE__*/_jsx(_Fragment, {
      children: frameworkTable
    }), showEditColumnsModal && /*#__PURE__*/_jsx(AsyncListingLibEditColumnsModal, Object.assign({}, editColumnsModalProps)), showExportModal && /*#__PURE__*/_jsx(AsyncListingLibExportModal, Object.assign({}, exportModalProps))]
  });
}
export default /*#__PURE__*/memo(CrmListingTable);